import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';
import { useToast } from '@/contexts/ToastContext';

const schema = z
  .object({
    email: z.string().email(),
    name: z.string().min(2, 'Name must be at least 2 characters'),
    phone: z.string().min(7, 'Please enter a valid phone number'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string().min(6),
    terms: z.boolean().refine((v) => v === true, { message: 'Required' }),
    privacy: z.boolean().refine((v) => v === true, { message: 'Required' }),
    marketing: z.boolean().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type FormData = z.infer<typeof schema>;

export default function RegisterPage() {
  const { t } = useTranslation();
  const { register: authRegister, needsEmailConfirmation } = useAuth();
  const navigate = useNavigate();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const { showToast } = useToast();
  const [error, setError] = useState('');
  const [registered, setRegistered] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { terms: false, privacy: false, marketing: false },
  });

  const onSubmit = async (data: FormData) => {
    setError('');
    const ok = await authRegister({
      email: data.email,
      name: data.name,
      phone: `+880 ${data.phone}`,
      password: data.password,
      marketingConsent: data.marketing || false,
    });
    if (ok) {
      if (needsEmailConfirmation) {
        setRegistered(true);
      } else {
        showToast(t('auth.register.success', 'Welcome to Rx.K Seoul!'));
        navigate(prefix + '/my');
      }
    } else {
      setError(t('auth.register.error', 'Registration failed. Please try again.'));
    }
  };

  if (registered) {
    return (
      <>
        <SharedNavbar />
        <div className="w-full min-h-screen bg-[#FAFAF9] pt-24 pb-20 flex items-center justify-center px-4">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-2xl p-6 md:p-10 border border-[#E8E8E6] flex flex-col items-center gap-4 text-center">
              <div className="w-14 h-14 rounded-full bg-[#F0EFED] flex items-center justify-center">
                <i className="ri-mail-send-line text-2xl text-[#7B1113]"></i>
              </div>
              <h1 className="font-serif text-2xl font-bold text-[#2A2A2A]">
                {t('auth.register.confirmTitle', 'Check your email')}
              </h1>
              <p className="text-sm text-[#8A8A8A]">
                {t(
                  'auth.register.confirmBody',
                  'We sent a confirmation link to your email. Sign in once you confirm.',
                )}
              </p>
              <Link
                to={prefix + '/login'}
                className="mt-2 px-7 py-3 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer"
              >
                {t('auth.forgot.backToLogin', 'Back to Sign In')}
              </Link>
            </div>
          </div>
        </div>
        <SharedFooter />
      </>
    );
  }

  return (
    <>
      <SharedNavbar />
      <div className="w-full min-h-screen bg-[#FAFAF9] pt-24 pb-20 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl p-6 md:p-10 border border-[#E8E8E6]">
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-[#2A2A2A] mb-2">
              {t('auth.register.title', 'Create Account')}
            </h1>
            <p className="text-sm text-[#8A8A8A] mb-8">
              {t('auth.register.subtitle', 'Join Rx.K Seoul for exclusive benefits')}
            </p>

            {error && (
              <div className="mb-5 p-3 rounded-xl bg-[#7B1113]/5 border border-[#7B1113]/10 text-sm text-[#7B1113] flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
              <div>
                <label className="block text-xs tracking-wider uppercase text-[#8A8A8A] mb-1.5">
                  {t('auth.register.email', 'Email')}
                </label>
                <input
                  type="email"
                  {...register('email')}
                  className="w-full px-4 py-3 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#7B1113] transition-colors h-11"
                  placeholder="your@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-[#7B1113]">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-xs tracking-wider uppercase text-[#8A8A8A] mb-1.5">
                  {t('auth.register.name', 'Full Name')}
                </label>
                <input
                  type="text"
                  {...register('name')}
                  className="w-full px-4 py-3 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#7B1113] transition-colors h-11"
                  placeholder="Your name"
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-[#7B1113]">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-xs tracking-wider uppercase text-[#8A8A8A] mb-1.5">
                  {t('auth.register.phone', 'Phone Number')}
                </label>
                <div className="flex">
                  <span className="flex items-center px-3 rounded-l-xl border border-r-0 border-[#E8E8E6] bg-[#F0EFED] text-sm text-[#8A8A8A] whitespace-nowrap">
                    +880
                  </span>
                  <input
                    type="tel"
                    {...register('phone')}
                    className="flex-1 px-4 py-3 rounded-r-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#7B1113] transition-colors h-11"
                    placeholder="1XXX-XXXXXX"
                  />
                </div>
                {errors.phone && (
                  <p className="mt-1 text-xs text-[#7B1113]">{errors.phone.message}</p>
                )}
              </div>

              <div>
                <label className="block text-xs tracking-wider uppercase text-[#8A8A8A] mb-1.5">
                  {t('auth.register.password', 'Password')}
                </label>
                <input
                  type="password"
                  {...register('password')}
                  className="w-full px-4 py-3 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#7B1113] transition-colors h-11"
                  placeholder="••••••••"
                />
                {errors.password && (
                  <p className="mt-1 text-xs text-[#7B1113]">{errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="block text-xs tracking-wider uppercase text-[#8A8A8A] mb-1.5">
                  {t('auth.register.confirmPassword', 'Confirm Password')}
                </label>
                <input
                  type="password"
                  {...register('confirmPassword')}
                  className="w-full px-4 py-3 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#7B1113] transition-colors h-11"
                  placeholder="••••••••"
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-xs text-[#7B1113]">{errors.confirmPassword.message}</p>
                )}
              </div>

              <div className="flex flex-col gap-2 mt-1">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    {...register('terms')}
                    className="accent-[#7B1113] w-4 h-4 mt-0.5 cursor-pointer"
                  />
                  <span className="text-xs text-[#8A8A8A] leading-relaxed">
                    {t('auth.register.terms', 'I agree to the Terms of Service')}
                  </span>
                </label>
                {errors.terms && (
                  <p className="text-xs text-[#7B1113]">{errors.terms.message}</p>
                )}

                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    {...register('privacy')}
                    className="accent-[#7B1113] w-4 h-4 mt-0.5 cursor-pointer"
                  />
                  <span className="text-xs text-[#8A8A8A] leading-relaxed">
                    {t('auth.register.privacy', 'I agree to the Privacy Policy')}
                  </span>
                </label>
                {errors.privacy && (
                  <p className="text-xs text-[#7B1113]">{errors.privacy.message}</p>
                )}

                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    {...register('marketing')}
                    className="accent-[#7B1113] w-4 h-4 mt-0.5 cursor-pointer"
                  />
                  <span className="text-xs text-[#8A8A8A] leading-relaxed">
                    {t('auth.register.marketing', 'I want to receive marketing emails and SMS (optional)')}
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors disabled:opacity-50 cursor-pointer text-center h-12 mt-2"
              >
                {isSubmitting ? t('common.loading') : t('auth.register.submit', 'Create Account')}
              </button>
            </form>

            <p className="mt-6 text-center text-xs text-[#8A8A8A]">
              {t('auth.register.hasAccount', 'Already have an account?')}{' '}
              <Link
                to={prefix + '/login'}
                className="text-[#7B1113] hover:underline font-medium"
              >
                {t('auth.register.loginLink', 'Sign in')}
              </Link>
            </p>
          </div>
        </div>
      </div>
      <SharedFooter />
    </>
  );
}